// API Service for connecting to the Laravel backend
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Admin Token management
const getAdminToken = () => localStorage.getItem('rork_admin_token');
const setAdminToken = (token: string) => localStorage.setItem('rork_admin_token', token);
const removeAdminToken = () => localStorage.removeItem('rork_admin_token');

// Customer Token management
const getCustomerToken = () => localStorage.getItem('rork_customer_token');
const setCustomerToken = (token: string) => localStorage.setItem('rork_customer_token', token);
const removeCustomerToken = () => localStorage.removeItem('rork_customer_token');

const PUBLIC_ADMIN_ENDPOINTS = new Set([
  '/login',
  '/login/pin',
  '/register',
  '/verify-email',
  '/email/verification-notification',
  '/auth/google',
  '/auth/google/callback',
  '/auth/google/token',
  '/health',
  '/platform/settings',
  '/pricing',
  '/billing/success',
  '/chat/send',
]);

const isPublicAdminEndpoint = (endpoint: string) => {
  if (PUBLIC_ADMIN_ENDPOINTS.has(endpoint)) {
    return true;
  }

  return endpoint.startsWith('/landing/')
    || endpoint.startsWith('/public/payments')
    || endpoint.startsWith('/chat/messages/')
    || endpoint.startsWith('/platform-marketers/')
    || endpoint === '/partners/apply';
};

// Request helper
async function fetchApi(endpoint: string, options: RequestInit = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  // Only add content type application/json if body is not FormData
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  // Determine which token to use
  // If endpoint starts with /public/store/, use customer token
  let token: string | null = null;
  if (endpoint.startsWith('/public/store/')) {
    token = getCustomerToken();
  } else if (!isPublicAdminEndpoint(endpoint)) {
    token = getAdminToken();
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const contentType = response.headers.get('content-type');
    let data: any;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'خطأ غير معروف في الخادم' };
    }

    if (!response.ok) {
      if (
        response.status === 403 &&
        (data.code === 'subscription_required' || data.code === 'paid_plan_required') &&
        window.location.pathname !== '/subscriptions'
      ) {
        window.location.assign('/subscriptions');
      }
      let errorMessage = 'حدث خطأ ما في الخادم';
      
      if (data.errors && typeof data.errors === 'object') {
        // Handle Laravel validation errors: get the first error from any field
        const firstField = Object.keys(data.errors)[0];
        if (firstField && Array.isArray(data.errors[firstField])) {
          errorMessage = data.errors[firstField][0];
        } else {
          errorMessage = JSON.stringify(data.errors);
        }
      } else if (typeof data.message === 'string') {
        errorMessage = data.message;
      } else if (typeof data.error === 'string') {
        errorMessage = data.error;
      } else if (typeof data.message === 'object') {
        errorMessage = JSON.stringify(data.message);
      }
      
      const apiError = new Error(errorMessage) as Error & {
        code?: string;
        reasons?: string[];
        suggestions?: string[];
        manualShippingAvailable?: boolean;
      };
      if (typeof data.code === 'string') {
        apiError.code = data.code;
      }
      if (Array.isArray(data.reasons)) apiError.reasons = data.reasons;
      if (Array.isArray(data.suggestions)) apiError.suggestions = data.suggestions;
      apiError.manualShippingAvailable = Boolean(data.manual_shipping_available);
      throw apiError;
    }

    return data;
  } catch (error: any) {
    console.error('API Error:', {
      url,
      method: options.method || 'GET',
      error: error.message
    });
    throw error;
  }
}

// Auth API (Merchant/Admin Only)
export const authApi = {
  login: async (email: string, password: string) => {
    const data = await fetchApi('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    const token = data.data?.token || data.token;
    if (token) {
      setAdminToken(token);
    }
    return data;
  },

  loginWithPin: async (email: string, pin: string) => {
    const data = await fetchApi('/login/pin', {
      method: 'POST',
      body: JSON.stringify({ email, pin }),
    });
    const token = data.data?.token || data.token;
    if (token) setAdminToken(token);
    return data;
  },

  security: () => fetchApi('/security'),

  updatePin: (data: { enabled: boolean; current_password: string; pin?: string; pin_confirmation?: string }) =>
    fetchApi('/security/pin', { method: 'PUT', body: JSON.stringify(data) }),

  register: async (userData: { name: string; email: string; password: string; phone?: string; marketer_ref?: string }) => {
    const data = await fetchApi('/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    const token = data.data?.token || data.token;
    if (token) {
      setAdminToken(token);
    }
    return data;
  },

  logout: () => {
    removeAdminToken();
  },

  getCurrentUser: async () => {
    return fetchApi('/me');
  },

  verifyEmail: async (email: string, token: string) => fetchApi('/verify-email', {
    method: 'POST',
    body: JSON.stringify({ email, token }),
  }),

  resendVerification: async (email: string) => fetchApi('/email/verification-notification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),

  updateProfile: async (data: any) => {
    return fetchApi('/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  isAuthenticated: () => !!getAdminToken(),
  getToken: getAdminToken,

  changePassword: async (data: any) => {
    return fetchApi('/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getGoogleAuthUrl: async () => {
    return fetchApi('/auth/google', {
      method: 'GET',
    });
  },

  loginWithGoogleCredential: async (credential: string) => {
    const data = await fetchApi('/auth/google/token', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
    if (data.data?.token) {
      setAdminToken(data.data.token);
    }
    return data;
  },
};

// Global export
export const productsApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; category?: string; status?: string; featured?: boolean; store_id?: number | string }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) queryParams.append(key, String(value));
      });
    }
    return fetchApi(`/products?${queryParams.toString()}`);
  },

  getById: async (id: number | string) => {
    return fetchApi(`/products/${id}`);
  },

  create: async (product: any) => {
    return fetchApi('/products', {
      method: 'POST',
      body: JSON.stringify(product),
    });
  },

  update: async (id: number | string, product: any) => {
    return fetchApi(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(product),
    });
  },

  delete: async (id: number | string) => {
    return fetchApi(`/products/${id}`, {
      method: 'DELETE',
    });
  },

  import: async (data: { url: string; platform: string; store_id: number | string; category_id?: number | string }) => {
    return fetchApi('/products/import', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  uploadImage: async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return fetchApi('/products/upload-image', {
      method: 'POST',
      body: formData,
    });
  },

  uploadDigitalFile: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi('/products/upload-digital-file', {
      method: 'POST',
      body: formData,
    });
  },

  getLicenseKeys: async (productId: string | number) => {
    return fetchApi(`/products/${productId}/license-keys`);
  },

  storeLicenseKeys: async (productId: string | number, keys: string[]) => {
    return fetchApi(`/products/${productId}/license-keys`, {
      method: 'POST',
      body: JSON.stringify({ keys }),
    });
  },

  deleteLicenseKey: async (productId: string | number, keyId: string | number) => {
    return fetchApi(`/products/${productId}/license-keys/${keyId}`, {
      method: 'DELETE',
    });
  },

  getDigitalDownloads: async (orderId: string | number) => {
    return fetchApi(`/orders/${orderId}/digital-downloads`);
  },
};

// General image upload (for any folder: avatars, logos, covers, favicons, etc.)
export const uploadImageApi = async (file: File, folder: string = 'uploads'): Promise<{ success: boolean; url: string }> => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('folder', folder);
  return fetchApi('/upload-image', {
    method: 'POST',
    body: formData,
  });
};

// Orders API
export const ordersApi = {
  getAll: async (params?: { page?: number; limit?: number; status?: string; search?: string; store_id?: number | string }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) queryParams.append(key, String(value));
      });
    }
    return fetchApi(`/orders?${queryParams.toString()}`);
  },

  getById: async (id: number | string) => {
    return fetchApi(`/orders/${id}`);
  },

  getShippingDraft: async (id: number | string) => {
    return fetchApi(`/orders/${id}/shipping-draft`);
  },

  create: async (order: any) => {
    return fetchApi('/orders', {
      method: 'POST',
      body: JSON.stringify(order),
    });
  },

  updateStatus: async (id: number | string, status: string) => {
    return fetchApi(`/orders/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },
  updatePaymentStatus: async (id: number | string, payment_status: string) => {
    return fetchApi(`/orders/${id}/payment-status`, {
      method: 'PUT',
      body: JSON.stringify({ payment_status }),
    });
  },
  approveLocalPayment: async (id: number | string) => {
    return fetchApi(`/orders/${id}/local-payment/approve`, {
      method: 'POST',
    });
  },
  rejectLocalPayment: async (id: number | string, reason?: string) => {
    return fetchApi(`/orders/${id}/local-payment/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// Customers API
export const customersApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; store_id?: number | string }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) queryParams.append(key, String(value));
      });
    }
    return fetchApi(`/customers?${queryParams.toString()}`);
  },

  getById: async (id: number | string) => {
    return fetchApi(`/customers/${id}`);
  },

  getOrders: async (customerId: number | string, storeId?: number | string) => {
    const query = storeId ? `?store_id=${storeId}` : '';
    return fetchApi(`/customers/${customerId}/orders${query}`);
  },

  broadcast: async (data: { store_id?: number | string; subject: string; content: string }) => {
    return fetchApi('/customers/broadcast', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ... keep other APIs as is, ensuring they point to either admin/merchant or customer logic ...

// Categories API
export const categoriesApi = {
  getAll: async (storeId?: number | string) => {
    const query = storeId ? `?store_id=${storeId}` : '';
    return fetchApi(`/categories${query}`);
  },

  create: async (category: any) => {
    return fetchApi('/categories', {
      method: 'POST',
      body: JSON.stringify(category),
    });
  },

  update: async (id: number | string, category: any) => {
    return fetchApi(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(category),
    });
  },

  delete: async (id: number | string) => {
    return fetchApi(`/categories/${id}`, {
      method: 'DELETE',
    });
  },
};

// Store Settings API
export const storeSettingsApi = {
  get: async () => fetchApi('/store'),
  update: async (data: any) => fetchApi('/store', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  getPaymentGateways: async () => fetchApi('/store/payment-gateways'),
  updatePaymentGateway: async (provider: string, data: any) => fetchApi(`/store/payment-gateways/${provider}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  saveTheme: async (themeId: string) => {
    // Extract numeric ID: 'theme-002' -> 2, '2' -> 2
    const match = String(themeId).match(/theme-0*(\d+)/i);
    const numericId = match ? parseInt(match[1]) : parseInt(themeId) || 1;

    return fetchApi('/store/theme', {
      method: 'POST',
      body: JSON.stringify({
        theme_id: numericId,
        theme_settings: JSON.stringify({ theme_id: numericId, original_theme_id: themeId }),
      }),
    });
  },
};

// Support API
export const supportApi = {
  getAll: async () => fetchApi('/support'),
  getById: async (id: number | string) => fetchApi(`/support/${id}`),
  createTicket: async (data: any) => fetchApi('/support', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  reply: async (id: number | string, message: string) => fetchApi(`/support/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
};

export const chatApi = {
  send: async (data: {
    session_id: string;
    message: string;
    name?: string;
    phone?: string;
    source?: string;
    page_url?: string;
  }) => fetchApi('/chat/send', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getMessages: async (sessionId: string) => fetchApi(`/chat/messages/${encodeURIComponent(sessionId)}`),
};

// Plans API
export const plansApi = {
  getAll: async () => {
    return fetchApi('/plans');
  },
  getById: async (id: number | string) => {
    return fetchApi(`/plans/${id}`);
  },
};

export const pricingApi = {
  getPlans: async () => fetchApi('/pricing'),
};

// Subscriptions API
export const subscriptionsApi = {
  getCurrent: async (storeId?: number | string) => {
    const query = storeId ? `?store_id=${storeId}` : '';
    return fetchApi(`/subscriptions/current${query}`);
  },
  subscribe: async (planId: number | string) => {
    return fetchApi(`/subscribe/${planId}`, {
      method: 'POST',
    });
  },
  create: async (data: { user_id: number | string; plan_id: number | string; store_id: number | string; type: string }) => {
    return fetchApi('/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// Coupons API
export const couponsApi = {
  getAll: async (storeId?: number | string) => {
    const query = storeId ? `?store_id=${storeId}` : '';
    return fetchApi(`/coupons${query}`);
  },

  create: async (coupon: any) => {
    return fetchApi('/coupons', {
      method: 'POST',
      body: JSON.stringify(coupon),
    });
  },

  update: async (id: number | string, coupon: any) => {
    return fetchApi(`/coupons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(coupon),
    });
  },

  delete: async (id: number | string) => {
    return fetchApi(`/coupons/${id}`, {
      method: 'DELETE',
    });
  },

  validate: async (code: string, orderAmount: number) => {
    return fetchApi('/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({ code, orderAmount }),
    });
  },
};

// Dashboard API
export const dashboardApi = {
  getStats: async (storeId?: number | string) => {
    const query = storeId ? `?store_id=${storeId}` : '';
    return fetchApi(`/dashboard/stats${query}`);
  },
};

// Landing Pages API
export const landingPagesApi = {
  getAll: async () => fetchApi('/landing-pages'),
  getById: async (id: number | string) => fetchApi(`/landing-pages/${id}`),
  getBySlug: async (slug: string) => fetchApi(`/landing/${slug}`, { cache: 'no-store' }),
  create: async (data: any) => fetchApi('/landing-pages', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: async (id: number | string, data: any) => fetchApi(`/landing-pages/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: async (id: number | string) => fetchApi(`/landing-pages/${id}`, {
    method: 'DELETE',
  }),
};

// Store API
export const storeApi = {
  getSettings: async () => {
    return fetchApi('/store');
  },

  updateSettings: async (settings: any) => {
    return fetchApi('/store', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },

  saveThemeSettings: async (themeSettings: any) => {
    return fetchApi('/store/theme', {
      method: 'POST',
      body: JSON.stringify({ 
        theme_settings: themeSettings 
      }),
    });
  },
};


// Admin API
export const adminApi = {
  getDashboardStats: async () => {
    return fetchApi('/admin/dashboard/stats');
  },
  getRecentActivity: async (limit: number = 10) => {
    return fetchApi(`/admin/dashboard/activity?limit=${limit}`);
  },
  settings: {
    get: async () => fetchApi('/admin/settings'),
    update: async (data: any) => fetchApi('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    getShippo: async () => fetchApi('/admin/settings/shippo'),
    updateShippo: async (apiToken: string) => fetchApi('/admin/settings/shippo', {
      method: 'PUT',
      body: JSON.stringify({ api_token: apiToken }),
    }),
    testShippo: async (apiToken?: string) => fetchApi('/admin/settings/shippo/test', {
      method: 'POST',
      body: JSON.stringify({ api_token: apiToken || undefined }),
    }),
    getImportProviders: async () => fetchApi('/admin/settings/product-import-providers'),
    updateImportProviders: async (data: Record<string, string>) => fetchApi('/admin/settings/product-import-providers', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  },
  stores: {
    getAll: async () => fetchApi('/stores'),
    create: async (data: any) => fetchApi('/stores', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: async (id: number | string, data: any) => fetchApi(`/stores/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    delete: async (id: number | string) => fetchApi(`/stores/${id}`, {
      method: 'DELETE',
    }),
  },
  users: {
    getAll: async () => fetchApi('/users'),
    sendEmail: async (data: { audience: 'all' | 'merchant' | 'customer'; subject: string; content: string }) =>
      fetchApi('/admin/users/email', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: async (id: number | string, data: any) => fetchApi(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    delete: async (id: number | string) => fetchApi(`/users/${id}`, {
      method: 'DELETE',
    }),
    getAvailableForStore: async () => fetchApi('/users/available-for-store'),
  },
  plans: {
    getAll: async () => fetchApi('/plans'),
    create: async (data: any) => fetchApi('/plans', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: async (id: number | string, data: any) => fetchApi(`/plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    delete: async (id: number | string) => fetchApi(`/plans/${id}`, {
      method: 'DELETE',
    }),
  },
  subscriptions: {
    getAll: async () => fetchApi('/subscriptions'),
    create: async (data: any) => fetchApi('/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: async (id: number | string, data: any) => fetchApi(`/subscriptions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    delete: async (id: number | string) => fetchApi(`/subscriptions/${id}`, {
      method: 'DELETE',
    }),
  },
  support: {
    getAll: async () => fetchApi('/admin/support'),
    resolve: async (id: number | string, message?: string) => fetchApi(`/admin/support/${id}/resolve`, {
      method: 'PUT',
      body: JSON.stringify({ message }),
    }),
  },
  chat: {
    getSessions: async () => fetchApi('/admin/chat/sessions'),
    getSession: async (sessionId: string) => fetchApi(`/admin/chat/sessions/${encodeURIComponent(sessionId)}`),
    reply: async (sessionId: string, message: string) => fetchApi(`/admin/chat/sessions/${encodeURIComponent(sessionId)}/reply`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),
  },
  whop: {
    getPlans: async () => fetchApi('/admin/whop/plans'),
    syncPlans: async () => fetchApi('/admin/whop/plans/sync', { method: 'POST' }),
  },
};

// Notifications API
export const notificationsApi = {
  getAll: async () => fetchApi('/notifications'),
  markAsRead: async (id: number | string) => fetchApi(`/notifications/${id}/mark-read`, { method: 'PUT' }),
  markAllAsRead: async () => fetchApi('/notifications/mark-all-read', { method: 'PUT' }),
  deleteAll: async () => fetchApi('/notifications/delete-all', { method: 'DELETE' }),
};

// Public Store API
export const publicStoreApi = {
  getStore: async (slug: string) => fetchApi(`/public/store/${slug}`),
  getProducts: async (slug: string, params?: { category_id?: number | string; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) queryParams.append(key, String(value));
      });
    }
    return fetchApi(`/public/store/${slug}/products?${queryParams.toString()}`);
  },
  getProduct: async (slug: string, productId: number | string) => fetchApi(`/public/store/${slug}/product/${productId}`),
  getCategories: async (slug: string) => fetchApi(`/public/store/${slug}/categories`),
  getPages: async (slug: string) => fetchApi(`/public/store/${slug}/pages`),
  getPage: async (slug: string, pageSlug: string) =>
    fetchApi(`/public/store/${slug}/page/${encodeURIComponent(pageSlug)}`),
  
  // Customer Auth
  register: async (slug: string, data: any) => {
    const res = await fetchApi(`/public/store/${slug}/register`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // @ts-ignore
    if (res.success && res.data?.token) setCustomerToken(res.data.token);
    return res;
  },
  login: async (slug: string, data: any) => {
    const res = await fetchApi(`/public/store/${slug}/login`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // @ts-ignore
    if (res.success && res.data?.token) setCustomerToken(res.data.token);
    return res;
  },
  resendVerification: async (email: string) => fetchApi('/email/verification-notification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
  
  // Customer Orders
  getShippingRates: async (slug: string, data: any) => fetchApi(`/public/store/${slug}/shipping/rates`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  createOrder: async (slug: string, data: any) => fetchApi(`/public/store/${slug}/orders`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getOrders: async (slug: string) => fetchApi(`/public/store/${slug}/orders`),
  confirmReceived: async (slug: string, orderId: number | string) => fetchApi(`/public/store/${slug}/orders/${orderId}/confirm-received`, {
    method: 'POST',
  }),
  getDigitalDownloads: async (slug: string, orderId: number | string) => fetchApi(`/public/store/${slug}/orders/${orderId}/digital-downloads`),
  
  // Customer Profile
  updateProfile: async (slug: string, data: any) => fetchApi(`/public/store/${slug}/profile`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  changePassword: async (slug: string, data: any) => fetchApi(`/public/store/${slug}/change-password`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // Coupons
  validateCoupon: async (slug: string, code: string, orderAmount: number) => fetchApi(`/public/store/${slug}/coupons/validate`, {
    method: 'POST',
    body: JSON.stringify({ code, order_amount: orderAmount }),
  }),

  // Affiliate
  trackAffiliate: async (slug: string, ref: string) => fetchApi(`/public/store/${slug}/affiliates/track?ref=${encodeURIComponent(ref)}`),
  affiliateRegister: async (slug: string, data: any) => fetchApi(`/public/store/${slug}/affiliates/register`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  affiliateLogin: async (slug: string, data: any) => fetchApi(`/public/store/${slug}/affiliates/login`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  affiliateDashboard: async (slug: string, token: string) => {
    const response = await fetch(`${API_BASE_URL}/public/store/${slug}/affiliates/dashboard`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Unable to load affiliate dashboard');
    }
    return data;
  },
};

// Health check
export const healthApi = {
  check: async () => {
    return fetchApi('/health');
  },
  getPlatformSettings: async () => {
    return fetchApi('/platform/settings');
  },
};

// Partners and marketers are the same underlying account (PlatformMarketer, distinguished
// by a `type` column) — these helpers are shared by both MarketersPage and PartnersPage via
// AffiliateAccountPanel, only the URL prefix and payload's `type` differ.
const affiliatePrefix = (type: 'marketer' | 'partner') => (type === 'partner' ? '/partners' : '/platform-marketers');

export const growthApi = {
  // Legacy lead-capture endpoint, kept for backward compatibility with old partner_applications data.
  applyAsPartner: (data: any) => fetchApi('/partners/apply', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  registerAffiliate: (type: 'marketer' | 'partner', data: any) => fetchApi(`${affiliatePrefix(type)}/register`, {
    method: 'POST',
    body: JSON.stringify({ ...data, type }),
  }),
  loginAffiliate: (type: 'marketer' | 'partner', data: { email: string; password: string }) => fetchApi(`${affiliatePrefix(type)}/login`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  affiliateDashboard: async (type: 'marketer' | 'partner', token: string) => {
    const response = await fetch(`${API_BASE_URL}${affiliatePrefix(type)}/dashboard`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'تعذر تحميل اللوحة');
    return data;
  },
  affiliateWithdraw: async (type: 'marketer' | 'partner', token: string, payload: any) => {
    const response = await fetch(`${API_BASE_URL}${affiliatePrefix(type)}/withdraw`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'تعذر إرسال طلب السحب');
    return data;
  },
  trackMarketer: (code: string) => fetchApi(`/platform-marketers/${encodeURIComponent(code)}/track`, {
    method: 'POST',
  }),
  adminPartners: () => fetchApi('/admin/growth/partners'),
  updatePartner: (id: number | string, data: any) => fetchApi(`/admin/growth/partners/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  adminMarketers: (type?: 'marketer' | 'partner') => fetchApi(`/admin/growth/marketers${type ? `?type=${type}` : ''}`),
  updateMarketer: (id: number | string, data: any) => fetchApi(`/admin/growth/marketers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  adminCommissions: (status: string = 'all') => fetchApi(`/admin/growth/commissions?status=${encodeURIComponent(status)}`),
  approveCommission: (id: number | string) => fetchApi(`/admin/growth/commissions/${id}/approve`, {
    method: 'POST',
  }),
};

// ─── Import Products API ──────────────────────────────────────────────────────
export const importProductsApi = {
  /**
   * Detect the platform from a URL without making an external fetch.
   */
  detect: async (url: string) => {
    return fetchApi('/import-products/detect', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  },

  /**
   * Fetch the external product page and return a preview object.
   * Returns { success, import_id, data: { name, price, images, ... } }
   */
  preview: async (url: string) => {
    return fetchApi('/import-products/preview', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  },

  /**
   * Confirm a previewed import – creates the product in the store.
   */
  confirm: async (payload: {
    import_id: number;
    name?: string;
    description?: string;
    price?: number;
    compare_price?: number;
    stock_quantity?: number;
    category_id?: number | null;
    images?: string[];
  }) => {
    return fetchApi('/import-products/confirm', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Paginated import history for the merchant's store.
   */
  history: async (params?: { page?: number; limit?: number; platform?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined) q.append(k, String(v));
      });
    }
    return fetchApi(`/import-products/history?${q.toString()}`);
  },

  /**
   * Get a single import record.
   */
  getById: async (id: number | string) => {
    return fetchApi(`/import-products/${id}`);
  },

  /**
   * Delete an import history record.
   */
  delete: async (id: number | string) => {
    return fetchApi(`/import-products/${id}`, { method: 'DELETE' });
  },
};

// ─── Affiliates API ───────────────────────────────────────────────────────────
export const affiliatesApi = {
  getAll: (storeId: number | string, params?: { page?: number; per_page?: number }) => {
    const q = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => v !== undefined && q.append(k, String(v)));
    return fetchApi(`/stores/${storeId}/affiliates?${q}`);
  },
  create: (storeId: number | string, data: any) => fetchApi(`/stores/${storeId}/affiliates`, { method: 'POST', body: JSON.stringify(data) }),
  update: (storeId: number | string, affiliateId: number | string, data: any) => fetchApi(`/stores/${storeId}/affiliates/${affiliateId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (storeId: number | string, affiliateId: number | string) => fetchApi(`/stores/${storeId}/affiliates/${affiliateId}`, { method: 'DELETE' }),
  approve: (storeId: number | string, affiliateId: number | string) => fetchApi(`/stores/${storeId}/affiliates/${affiliateId}/approve`, { method: 'POST' }),
  getCommissions: (storeId: number | string) => fetchApi(`/stores/${storeId}/affiliate-commissions`),
  payCommission: (storeId: number | string, commissionId: number | string, reference?: string) =>
    fetchApi(`/stores/${storeId}/affiliate-commissions/${commissionId}/pay`, { method: 'POST', body: JSON.stringify({ reference }) }),
  getStats: (storeId: number | string) => fetchApi(`/stores/${storeId}/affiliate-stats`),
};

// ─── Campaigns API ────────────────────────────────────────────────────────────
export const campaignsApi = {
  getAll: (storeId: number | string) => fetchApi(`/stores/${storeId}/campaigns`),
  create: (storeId: number | string, data: any) => fetchApi(`/stores/${storeId}/campaigns`, { method: 'POST', body: JSON.stringify(data) }),
  update: (storeId: number | string, id: number | string, data: any) => fetchApi(`/stores/${storeId}/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (storeId: number | string, id: number | string) => fetchApi(`/stores/${storeId}/campaigns/${id}`, { method: 'DELETE' }),
  send: (storeId: number | string, id: number | string) => fetchApi(`/stores/${storeId}/campaigns/${id}/send`, { method: 'POST' }),
  getStats: (storeId: number | string, id: number | string) => fetchApi(`/stores/${storeId}/campaigns/${id}/stats`),
};

// ─── Automation Rules API ─────────────────────────────────────────────────────
export const automationRulesApi = {
  getAll: (storeId: number | string) => fetchApi(`/stores/${storeId}/automation-rules`),
  create: (storeId: number | string, data: any) => fetchApi(`/stores/${storeId}/automation-rules`, { method: 'POST', body: JSON.stringify(data) }),
  update: (storeId: number | string, id: number | string, data: any) => fetchApi(`/stores/${storeId}/automation-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (storeId: number | string, id: number | string) => fetchApi(`/stores/${storeId}/automation-rules/${id}`, { method: 'DELETE' }),
  toggle: (storeId: number | string, id: number | string) => fetchApi(`/stores/${storeId}/automation-rules/${id}/toggle`, { method: 'POST' }),
};

// ─── Templates API ────────────────────────────────────────────────────────────
export const templatesApi = {
  getStoreTemplates: (storeId: number | string) => fetchApi(`/stores/${storeId}/templates`),
  getMarketplace: () => fetchApi('/templates/marketplace'),
  purchase: (storeId: number | string, templateId: number | string) =>
    fetchApi(`/stores/${storeId}/templates/${templateId}/purchase`, { method: 'POST' }),
  apply: (storeId: number | string, templateId: number | string) =>
    fetchApi(`/stores/${storeId}/templates/${templateId}/apply`, { method: 'POST' }),
  getCurrent: (storeId: number | string) => fetchApi(`/stores/${storeId}/templates/current`),
};

// ─── Pages API ────────────────────────────────────────────────────────────────
export const storePageApi = {
  getAll: (storeId: number | string) => fetchApi(`/stores/${storeId}/pages`),
  create: (storeId: number | string, data: any) => fetchApi(`/stores/${storeId}/pages`, { method: 'POST', body: JSON.stringify(data) }),
  getById: (storeId: number | string, pageId: number | string) => fetchApi(`/stores/${storeId}/pages/${pageId}`),
  update: (storeId: number | string, pageId: number | string, data: any) => fetchApi(`/stores/${storeId}/pages/${pageId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (storeId: number | string, pageId: number | string) => fetchApi(`/stores/${storeId}/pages/${pageId}`, { method: 'DELETE' }),
  getByType: (storeId: number | string, type: string) => fetchApi(`/stores/${storeId}/pages/type/${type}`),
};

// ─── Domains API ──────────────────────────────────────────────────────────────
export const domainsApi = {
  getAll: (storeId: number | string) => fetchApi(`/stores/${storeId}/domains`),
  create: (storeId: number | string, data: any) => fetchApi(`/stores/${storeId}/domains`, { method: 'POST', body: JSON.stringify(data) }),
  verify: (storeId: number | string, domainId: number | string) =>
    fetchApi(`/stores/${storeId}/domains/${domainId}/verify`, { method: 'POST' }),
  delete: (storeId: number | string, domainId: number | string) => fetchApi(`/stores/${storeId}/domains/${domainId}`, { method: 'DELETE' }),
};

// ─── Visual Identity API ──────────────────────────────────────────────────────
export const visualIdentityApi = {
  get: (storeId: number | string) => fetchApi(`/stores/${storeId}/visual-identity`),
  update: (storeId: number | string, data: any) => fetchApi(`/stores/${storeId}/visual-identity`, { method: 'PUT', body: JSON.stringify(data) }),
  uploadLogo: (storeId: number | string, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return fetchApi(`/stores/${storeId}/visual-identity/logo`, { method: 'POST', body: formData });
  },
};

// ─── Page Builder API ─────────────────────────────────────────────────────────
export const pageBuilderApi = {
  getAll: (storeId: number | string) => fetchApi(`/stores/${storeId}/page-builder-sections`),
  create: (storeId: number | string, data: any) => fetchApi(`/stores/${storeId}/page-builder-sections`, { method: 'POST', body: JSON.stringify(data) }),
  update: (storeId: number | string, sectionId: number | string, data: any) =>
    fetchApi(`/stores/${storeId}/page-builder-sections/${sectionId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (storeId: number | string, sectionId: number | string) =>
    fetchApi(`/stores/${storeId}/page-builder-sections/${sectionId}`, { method: 'DELETE' }),
  reorder: (storeId: number | string, sections: any[]) =>
    fetchApi(`/stores/${storeId}/page-builder-sections/reorder`, { method: 'POST', body: JSON.stringify({ sections }) }),
};

// ─── Reports API ──────────────────────────────────────────────────────────────
export const reportsApi = {
  getAll: (storeId: number | string) => fetchApi(`/stores/${storeId}/reports`),
  create: (storeId: number | string, data: any) => fetchApi(`/stores/${storeId}/reports`, { method: 'POST', body: JSON.stringify(data) }),
  getById: (storeId: number | string, reportId: number | string) => fetchApi(`/stores/${storeId}/reports/${reportId}`),
  delete: (storeId: number | string, reportId: number | string) => fetchApi(`/stores/${storeId}/reports/${reportId}`, { method: 'DELETE' }),
  getDashboardStats: (storeId: number | string) => fetchApi(`/stores/${storeId}/dashboard-stats`),
};

// ─── Activity Logs API ────────────────────────────────────────────────────────
export const activityLogsApi = {
  getAll: (storeId: number | string, params?: { page?: number; search?: string }) => {
    const q = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => v !== undefined && q.append(k, String(v)));
    return fetchApi(`/stores/${storeId}/activity-logs?${q}`);
  },
  getById: (storeId: number | string, logId: number | string) => fetchApi(`/stores/${storeId}/activity-logs/${logId}`),
};

// ─── Wallet API ───────────────────────────────────────────────────────────────
export const walletApi = {
  get: (storeId: number | string) =>
    fetchApi(`/stores/${storeId}/wallet`),

  getTransactions: (storeId: number | string, type?: string, params?: { status?: string; from_date?: string; to_date?: string; per_page?: number }) => {
    const q = new URLSearchParams();
    if (type)              q.append('type',      type);
    if (params?.status)    q.append('status',    params.status);
    if (params?.from_date) q.append('from_date', params.from_date);
    if (params?.to_date)   q.append('to_date',   params.to_date);
    if (params?.per_page)  q.append('per_page',  String(params.per_page));
    return fetchApi(`/stores/${storeId}/wallet/transactions?${q}`);
  },

  deposit: (storeId: number | string, data: { amount: number; payment_method?: string; currency?: string }) =>
    fetchApi(`/stores/${storeId}/wallet/deposit`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  checkDepositStatus: (storeId: number | string, paymentIntentId: string) =>
    fetchApi(`/stores/${storeId}/wallet/deposit/${paymentIntentId}/status`),

  withdraw: (storeId: number | string, data: {
    amount: number;
    account_holder_name: string;
    bank_name: string;
    iban: string;
    reason?: string;
  }) =>
    fetchApi(`/stores/${storeId}/wallet/withdraw`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getAdminWithdrawals: (status = 'all') =>
    fetchApi(`/admin/wallet/withdrawals?status=${encodeURIComponent(status)}`),

  completeAdminWithdrawal: (transactionId: number | string) =>
    fetchApi(`/admin/wallet/withdrawals/${transactionId}/complete`, { method: 'POST' }),

  rejectAdminWithdrawal: (transactionId: number | string, reason: string) =>
    fetchApi(`/admin/wallet/withdrawals/${transactionId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  exportTransactions: (storeId: number | string, params?: { from_date?: string; to_date?: string }) => {
    const q = new URLSearchParams();
    if (params?.from_date) q.append('from_date', params.from_date);
    if (params?.to_date)   q.append('to_date',   params.to_date);
    return fetchApi(`/stores/${storeId}/wallet/export?${q}`);
  },
};

// ─── Backups API ──────────────────────────────────────────────────────────────
export const backupsApi = {
  getAll: (storeId: number | string) => fetchApi(`/stores/${storeId}/backups`),
  create: (storeId: number | string, data: { includes: Record<string, boolean>; type?: string }) =>
    fetchApi(`/stores/${storeId}/backups`, { method: 'POST', body: JSON.stringify(data) }),
  getById: (storeId: number | string, backupId: number | string) => fetchApi(`/stores/${storeId}/backups/${backupId}`),
  delete: (storeId: number | string, backupId: number | string) => fetchApi(`/stores/${storeId}/backups/${backupId}`, { method: 'DELETE' }),
  restore: (storeId: number | string, backupId: number | string) =>
    fetchApi(`/stores/${storeId}/backups/${backupId}/restore`, { method: 'POST' }),
  download: async (storeId: number | string, backupId: number | string) => {
    const response = await fetch(`${API_BASE_URL}/stores/${storeId}/backups/${backupId}/download`, {
      headers: {
        Accept: 'application/zip',
        Authorization: `Bearer ${getAdminToken() || ''}`,
      },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'فشل تنزيل النسخة الاحتياطية');
    }
    return response.blob();
  },
  schedule: (storeId: number | string, data: { frequency: string; time?: string; enabled: boolean }) =>
    fetchApi(`/stores/${storeId}/backups/schedule`, { method: 'POST', body: JSON.stringify(data) }),
};

// ─── Shipping API ─────────────────────────────────────────────────────────────
export const shippingApi = {
  // Carriers (platform-level)
  getCarriers: (params?: { active_only?: boolean; type?: string; store_id?: number | string }) => {
    const q = new URLSearchParams();
    if (params?.active_only) q.append('active_only', '1');
    if (params?.type) q.append('type', params.type);
    if (params?.store_id) q.append('store_id', String(params.store_id));
    return fetchApi(`/shipping/carriers?${q}`);
  },
  createCarrier: (data: any) => fetchApi('/shipping/carriers', { method: 'POST', body: JSON.stringify(data) }),
  updateCarrier: (id: number | string, data: any) => fetchApi(`/shipping/carriers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCarrier: (id: number | string) => fetchApi(`/shipping/carriers/${id}`, { method: 'DELETE' }),
  toggleCarrier: (id: number | string) => fetchApi(`/shipping/carriers/${id}/toggle`, { method: 'POST' }),
  connectCarrier: (storeId: number | string, id: number | string) =>
    fetchApi(`/stores/${storeId}/shipping/carriers/${id}/connect`, { method: 'POST' }),
  disconnectCarrier: (storeId: number | string, id: number | string) =>
    fetchApi(`/stores/${storeId}/shipping/carriers/${id}/connect`, { method: 'DELETE' }),

  // Shipments (per-store)
  getShipments: (storeId: number | string, params?: { status?: string; search?: string; carrier_id?: number | string; per_page?: number }) => {
    const q = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => v !== undefined && q.append(k, String(v)));
    return fetchApi(`/stores/${storeId}/shipments?${q}`);
  },
  createShipment: (storeId: number | string, data: any) =>
    fetchApi(`/stores/${storeId}/shipments`, { method: 'POST', body: JSON.stringify(data) }),
  getShipment: (storeId: number | string, id: number | string) =>
    fetchApi(`/stores/${storeId}/shipments/${id}`),
  updateStatus: (storeId: number | string, id: number | string, status: string, description?: string) =>
    fetchApi(`/stores/${storeId}/shipments/${id}/status`, { method: 'PUT', body: JSON.stringify({ status, description }) }),
  deleteShipment: (storeId: number | string, id: number | string) =>
    fetchApi(`/stores/${storeId}/shipments/${id}`, { method: 'DELETE' }),
  cancelShipment: (storeId: number | string, id: number | string) =>
    fetchApi(`/stores/${storeId}/shipments/${id}/cancel`, { method: 'POST' }),
  schedulePickup: (storeId: number | string, id: number | string, data: any) =>
    fetchApi(`/stores/${storeId}/shipments/${id}/pickup`, { method: 'POST', body: JSON.stringify(data) }),

  // Dashboard & tools
  getDashboard: (storeId: number | string) => fetchApi(`/stores/${storeId}/shipping/dashboard`),
  track: (storeId: number | string, number: string) =>
    fetchApi(`/stores/${storeId}/shipping/track?number=${encodeURIComponent(number)}`),
  calculate: (storeId: number | string, data: { weight: number; carrier_id: number; country: string }) =>
    fetchApi(`/stores/${storeId}/shipping/calculate`, { method: 'POST', body: JSON.stringify(data) }),
  getShippoRates: (storeId: number | string, data: any) =>
    fetchApi(`/stores/${storeId}/shipping/shippo/rates`, { method: 'POST', body: JSON.stringify(data) }),
  getShippoCarriers: (storeId: number | string) =>
    fetchApi(`/stores/${storeId}/shipping/shippo/carriers`),
  getSettings: (storeId: number | string) => fetchApi(`/stores/${storeId}/shipping/settings`),
  updateSettings: (storeId: number | string, data: any) =>
    fetchApi(`/stores/${storeId}/shipping/settings`, { method: 'PUT', body: JSON.stringify(data) }),
};

export default {
  auth: authApi,
  products: productsApi,
  orders: ordersApi,
  customers: customersApi,
  categories: categoriesApi,
  plans: plansApi,
  pricing: pricingApi,
  coupons: couponsApi,
  dashboard: dashboardApi,
  store: storeApi,
  chat: chatApi,
  admin: adminApi,
  notifications: notificationsApi,
  subscriptions: subscriptionsApi,
  health: healthApi,
  publicStore: publicStoreApi,
  importProducts: importProductsApi,
  landingPages: landingPagesApi,
  affiliates: affiliatesApi,
  campaigns: campaignsApi,
  automationRules: automationRulesApi,
  templates: templatesApi,
  storePages: storePageApi,
  domains: domainsApi,
  visualIdentity: visualIdentityApi,
  pageBuilder: pageBuilderApi,
  reports: reportsApi,
  activityLogs: activityLogsApi,
  wallet: walletApi,
  backups: backupsApi,
  shipping: shippingApi,
  growth: growthApi,
};
